import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConstructionProjectSetupService } from '../services/construction-setup.service';
import type { ConstructionProjectSettings, ConstructionProjectMember } from '../types/construction-setup.types';
import { toast } from 'sonner';

export function useConstructionSetup(projectId: string) {
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ['construction-setup', 'settings', projectId],
    queryFn: () => ConstructionProjectSetupService.getSettings(projectId),
    enabled: !!projectId,
  });

  const checksQuery = useQuery({
    queryKey: ['construction-setup', 'checks', projectId],
    queryFn: () => ConstructionProjectSetupService.getSetupChecks(projectId),
    enabled: !!projectId,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (updates: Partial<ConstructionProjectSettings>) => 
      ConstructionProjectSetupService.updateSettings(projectId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-setup', 'settings', projectId] });
      toast.success('Cập nhật cấu hình dự án thành công');
    },
  });

  const completeCheckMutation = useMutation({
    mutationFn: ({ checkId, userId }: { checkId: string, userId: string }) => 
      ConstructionProjectSetupService.completeSetupCheck(checkId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-setup', 'checks', projectId] });
      toast.success('Đã xác nhận hoàn thành bước thiết lập');
    },
  });

  return {
    settings: settingsQuery.data,
    isLoadingSettings: settingsQuery.isLoading,
    checks: checksQuery.data || [],
    isLoadingChecks: checksQuery.isLoading,
    updateSettings: updateSettingsMutation.mutateAsync,
    isUpdatingSettings: updateSettingsMutation.isPending,
    completeCheck: completeCheckMutation.mutateAsync,
    isCompletingCheck: completeCheckMutation.isPending,
  };
}

export function useConstructionMembers(projectId: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['construction-setup', 'members', projectId],
    queryFn: () => ConstructionProjectSetupService.getMembers(projectId),
    enabled: !!projectId,
  });

  const addMemberMutation = useMutation({
    mutationFn: (data: Partial<ConstructionProjectMember>) => 
      ConstructionProjectSetupService.addMember(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-setup', 'members', projectId] });
      toast.success('Đã gán nhân sự vào dự án');
    },
  });

  return {
    members: query.data || [],
    isLoading: query.isLoading,
    addMember: addMemberMutation.mutateAsync,
    isAdding: addMemberMutation.isPending,
  };
}

export function useConstructionDocuments(projectId: string) {
  return useQuery({
    queryKey: ['construction-setup', 'documents', projectId],
    queryFn: () => ConstructionProjectSetupService.getDocuments(projectId),
    enabled: !!projectId,
  });
}
